import React, { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../../hooks/useIsMobile';

interface FreePublishModalProps {
  open: boolean;
  onClose: () => void;
  /** The preview URL that will become the public URL */
  previewUrl: string;
  /** Called when user confirms — should do the actual publish */
  onConfirm: () => Promise<void>;
}

/**
 * Lightweight confirmation modal for free publish.
 * Shows the URL and lets the user confirm with one click.
 */
const FreePublishModal: React.FC<FreePublishModalProps> = ({
  open,
  onClose,
  previewUrl,
  onConfirm,
}) => {
  const [publishing, setPublishing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const isMobile = useIsMobile();

  const displayUrl = useMemo(() => {
    const url = /^https?:\/\//i.test(previewUrl) ? previewUrl : `https://${previewUrl}`;
    try {
      return new URL(url).host;
    } catch {
      return previewUrl;
    }
  }, [previewUrl]);

  const fullUrl = useMemo(() => {
    return /^https?:\/\//i.test(previewUrl) ? previewUrl : `https://${previewUrl}`;
  }, [previewUrl]);

  const handleConfirm = useCallback(async () => {
    setPublishing(true);
    try {
      await onConfirm();
      setSuccess(true);
    } catch {
      // Error is handled by parent (toast)
      onClose();
    } finally {
      setPublishing(false);
    }
  }, [onConfirm, onClose]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [fullUrl]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !publishing) {
      onClose();
    }
  }, [onClose, publishing]);

  if (!open) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[10000] animate-slide-in-center w-[calc(100vw-32px)] max-w-md">
        <div className="bg-[#1e293b]/90 backdrop-blur-xl border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">

          {!success ? (
            /* ── Confirm Step ── */
            <div className="p-6">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-[#22c55e]/10 border border-[#22c55e]/20 flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-[#22c55e]">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-5-5 1.41-1.41L11 14.17l7.59-7.59L20 8l-9 9z" fill="currentColor"/>
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-xl font-bold text-white text-center mb-2">
                Publish your app
              </h2>
              <p className="text-sm text-white/60 text-center mb-5">
                Anyone with this link will be able to access your app.
              </p>

              {/* URL Display */}
              <div className="bg-black/30 border border-white/10 rounded-lg px-4 py-3 mb-5">
                <div className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-[#22c55e] flex-shrink-0">
                    <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <span className="text-white font-mono text-sm truncate">{displayUrl}</span>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={publishing}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 text-white/70 text-sm font-medium hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={publishing}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-semibold text-sm border border-[#22c55e]/30 shadow-lg shadow-[#22c55e]/20 hover:from-[#16a34a] hover:to-[#15803d] transition-all disabled:opacity-70"
                >
                  {publishing ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Publishing…
                    </span>
                  ) : (
                    'Publish'
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* ── Success Step ── */
            <div className="p-6">
              {/* Animated check */}
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-[#22c55e]/15 border-2 border-[#22c55e]/40 flex items-center justify-center animate-[scale-in_0.3s_ease-out]">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-[#22c55e]">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>

              <h2 className="text-xl font-bold text-white text-center mb-2">
                Your app is live!
              </h2>
              <p className="text-sm text-white/60 text-center mb-5">
                Share this link with anyone to let them use your app.
              </p>

              {/* URL with copy */}
              <div className="bg-black/30 border border-white/10 rounded-lg px-4 py-3 mb-5 flex items-center justify-between gap-2">
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#22c55e] font-mono text-sm truncate hover:underline"
                >
                  {displayUrl}
                </a>
                <button
                  onClick={handleCopy}
                  className="flex-shrink-0 p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                  title="Copy URL"
                >
                  {copied ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#22c55e]">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 text-white/70 text-sm font-medium hover:bg-white/5 transition-colors"
                >
                  Close
                </button>
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-white/10 border border-white/10 text-white text-sm font-medium text-center hover:bg-white/15 transition-colors"
                >
                  Open App
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return isMobile ? createPortal(modalContent, document.body) : modalContent;
};

export default FreePublishModal;
